import { isEmpty } from 'lodash'

import { PortfolioFormChangeValues } from 'types'

import PDSAssetTag from 'designSystem/components/PDSAssetTag/PDSAssetTag'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSIcon from 'designSystem/components/PDSIcon/PDSIcon'
import PDSSeparator from 'designSystem/components/PDSSeparator/PDSSeparator'
import PDSSubCard from 'designSystem/components/PDSSubCard/PDSSubCard'
import PDSSwitch from 'designSystem/components/PDSSwitch/PDSSwitch'
import PDSTag from 'designSystem/components/PDSTag/PDSTag'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'

type Props = {
  label: string
  change: PortfolioFormChangeValues
}

const TAG_LABELS = new Set([
  'Excluded Sectors',
  'Excluded Industries',
  'Excluded Assets',
])

const SWITCH_LABELS = new Set([
  'Automation',
  'Enable Tax Optimization',
  'Fractional Shares',
])

export default function InvestmentPreferenceChangeItem({
  label,
  change,
}: Props) {
  const { oldValue, newValue } = change

  const createAssetsChangeContent = (value: string, checked: boolean) => {
    return !isEmpty(value) ? (
      <PDSSubCard>
        <PDSFlexBox
          flexBasis="column"
          gap={SIZE.s8}
          width="7.5rem"
          height="1.5rem"
        >
          <PDSFlexBox width="4rem">
            <PDSAssetTag symbol={value} />
          </PDSFlexBox>
          <PDSSwitch checked={checked} readOnly />
        </PDSFlexBox>
      </PDSSubCard>
    ) : (
      <PDSText
        variant="p"
        fontSize={FONT_SIZE.xs}
        color={COLOR.textTertiary}
        fontWeight={500}
      >
        None
      </PDSText>
    )
  }

  const createSwitchChangeContent = (value: string) => {
    return !isEmpty(value) ? (
      <PDSBox>
        <PDSSwitch
          disabled={true}
          checked={value === 'On'}
          onText="On"
          offText="Off"
          readOnly
        />
      </PDSBox>
    ) : (
      <PDSText
        variant="p"
        fontSize={FONT_SIZE.xs}
        color={COLOR.textTertiary}
        fontWeight={500}
      >
        None
      </PDSText>
    )
  }

  const createTagChangeContent = (values: string[]) => {
    return values.length >= 1 ? (
      <PDSFlexBox flexDirection="column" gap={SIZE.s4}>
        {values.map((tag, i) => (
          <PDSTag key={`change-tag-${i}`} variant="alert">
            {tag}
          </PDSTag>
        ))}
      </PDSFlexBox>
    ) : (
      <PDSText
        variant="p"
        fontSize={FONT_SIZE.xs}
        color={COLOR.textTertiary}
        fontWeight={500}
      >
        None
      </PDSText>
    )
  }

  let content = null
  if (SWITCH_LABELS.has(label)) {
    content = (
      <PDSFlexBox justifyContent="flex-start">
        <PDSFlexBox flex={1} justifyContent="space-between">
          {createSwitchChangeContent(oldValue)}
          <PDSIcon name="arrowRight" size="md" />
        </PDSFlexBox>

        <PDSFlexBox flex={1}>{createSwitchChangeContent(newValue)}</PDSFlexBox>
      </PDSFlexBox>
    )
  } else if (TAG_LABELS.has(label)) {
    const oldTags = !isEmpty(oldValue) ? oldValue.split('\n') : []
    const newTags = !isEmpty(newValue) ? newValue.split('\n') : []
    content = (
      <PDSFlexBox justifyContent="flex-start">
        <PDSFlexBox flex={1} justifyContent="space-between" alignItems="center">
          {createTagChangeContent(oldTags)}
          <PDSIcon name="arrowRight" size="md" />
        </PDSFlexBox>
        <PDSFlexBox flex={1}>
          <PDSFlexBox flexDirection="column" alignItems="center">
            {createTagChangeContent(newTags)}
          </PDSFlexBox>
        </PDSFlexBox>
      </PDSFlexBox>
    )
  } else if (label === 'Assets') {
    const values = !isEmpty(oldValue) ? oldValue.split(',') : []
    content = (
      <PDSFlexBox
        justifyContent="flex-start"
        flexDirection="column"
        gap={SIZE.s8}
      >
        {values.map((value, i) => {
          const [label, wasChecked] = value?.split(' ')
          return (
            <PDSFlexBox key={`change-asset-${i}`} justifyContent="flex-start">
              <PDSFlexBox
                flex={1}
                justifyContent="space-between"
                alignItems="center"
              >
                {createAssetsChangeContent(label, wasChecked === '0')}
                <PDSIcon name="arrowRight" size="md" />
              </PDSFlexBox>
              <PDSFlexBox flex={1}>
                <PDSFlexBox flexDirection="column" alignItems="center">
                  {createAssetsChangeContent(label, wasChecked === '1')}
                </PDSFlexBox>
              </PDSFlexBox>
            </PDSFlexBox>
          )
        })}
      </PDSFlexBox>
    )
  } else {
    content = (
      <PDSFlexBox justifyContent="flex-start">
        <PDSFlexBox flex={1} justifyContent="space-between">
          <PDSText
            variant="p"
            fontSize={FONT_SIZE.xs}
            color={COLOR.textTertiary}
            fontWeight={500}
          >
            {!isEmpty(oldValue) ? oldValue : 'None'}
          </PDSText>
          <PDSIcon name="arrowRight" size="md" />
        </PDSFlexBox>
        <PDSFlexBox flex={1}>
          <PDSText
            variant="p"
            fontSize={FONT_SIZE.xs}
            color={COLOR.textPrimary}
            fontWeight={500}
          >
            {!isEmpty(newValue) ? newValue : 'None'}
          </PDSText>
        </PDSFlexBox>
      </PDSFlexBox>
    )
  }

  return (
    <PDSFlexBox flexDirection="column" gap={SIZE.s4}>
      <PDSText variant="p" fontSize={FONT_SIZE.xs} fontWeight={600}>
        {label}
      </PDSText>
      <PDSSeparator />
      {content}
    </PDSFlexBox>
  )
}
